import { OpenFeature, ProviderEvents } from '@openfeature/web-sdk';
import type { JsonValue } from '@openfeature/web-sdk';
import { useSyncExternalStore } from 'react';
import { getFlagOverrides } from './overrides';
import { getDatadogProvider } from './providerState';
import type { FlagType, ResolvedFlag } from './types';

/**
 * Imperative flag read for non-component code — utils, or shared libraries that may
 * render in a provider-less host. Reads the global OpenFeature client: no
 * `<OpenFeatureProvider>` needed, never throws (returns the default when no provider
 * is registered), not reactive. The value type follows the `defaultValue` you pass,
 * so it works for any flag type and any key.
 *
 * @example
 * ```ts
 * if (getFlag('google_advanced_markers')) { ... }   // boolean (default false)
 * const color = getFlag('map-theme', 'blue');        // string
 * ```
 */
export function getFlag(flagKey: string, defaultValue?: boolean): boolean;
export function getFlag(flagKey: string, defaultValue: string): string;
export function getFlag(flagKey: string, defaultValue: number): number;
export function getFlag<T extends object>(flagKey: string, defaultValue: T): T;
export function getFlag(
  flagKey: string,
  defaultValue: boolean | string | number | object = false,
): boolean | string | number | object {
  const client = OpenFeature.getClient();
  switch (typeof defaultValue) {
    case 'string':
      return client.getStringValue(flagKey, defaultValue);
    case 'number':
      return client.getNumberValue(flagKey, defaultValue);
    case 'object':
      return client.getObjectValue(
        flagKey,
        defaultValue as unknown as JsonValue,
      ) as object;
    default:
      return client.getBooleanValue(flagKey, defaultValue as boolean);
  }
}

// Minimal read-side shape of the Datadog provider's fetched precompute config.
interface PrecomputedFlag {
  variationType: FlagType;
  variationValue: boolean | string | number | object;
}
type ProviderWithConfig = {
  flagsConfiguration?: {
    precomputed?: {
      response?: {
        data?: { attributes?: { flags?: Record<string, PrecomputedFlag> } };
      };
    };
  };
};

/**
 * The flags the Datadog endpoint actually returned for this environment — the real
 * list, not a code-side registry. Empty when the endpoint is unreachable (no creds /
 * fetch not done), which is intentional: a flag not client-side-available for an env
 * won't appear, so half-built features can't leak into higher envs via the panel.
 */
export const getResolvedFlags = (): ResolvedFlag[] => {
  const flags = (getDatadogProvider() as ProviderWithConfig | null)
    ?.flagsConfiguration?.precomputed?.response?.data?.attributes?.flags;
  if (!flags) return [];
  return Object.entries(flags)
    .map(([key, flag]) => ({
      key,
      type: flag.variationType,
      value: flag.variationValue,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
};

/**
 * Every flag with a value in this environment as a `{ key: value }` map — what the
 * Datadog endpoint returned, with local overrides applied on top. Backs the console
 * API's `list()`.
 *
 * Not a gating tool: it carries no read-site default, so an absent key and an off
 * flag are indistinguishable. Gate on {@link getFlag} or a `use*FlagValue` hook.
 */
export const getFlags = (): Record<string, unknown> => ({
  ...Object.fromEntries(
    getResolvedFlags().map(({ key, value }) => [key, value]),
  ),
  ...getFlagOverrides(),
});

/**
 * Subscribes to flag-config changes (endpoint config ready, or a local override
 * toggled). Returns an unsubscribe fn.
 */
export const onFlagsChanged = (callback: () => void): (() => void) => {
  OpenFeature.addHandler(ProviderEvents.Ready, callback);
  OpenFeature.addHandler(ProviderEvents.ConfigurationChanged, callback);
  return () => {
    OpenFeature.removeHandler(ProviderEvents.Ready, callback);
    OpenFeature.removeHandler(ProviderEvents.ConfigurationChanged, callback);
  };
};

// A change token: useSyncExternalStore re-renders by comparing snapshots. Bumped
// inside subscribe so it's current before React re-reads, rather than depending on
// OpenFeature invoking handlers in registration order.
let flagsVersion = 0;

const subscribeToFlagChanges = (onStoreChange: () => void) =>
  onFlagsChanged(() => {
    flagsVersion += 1;
    onStoreChange();
  });

const getFlagsVersion = () => flagsVersion;

/**
 * Re-renders the caller when any flag changes. Pair it with an imperative
 * {@link getFlag} read, which has no subscription of its own; the typed
 * `use*FlagValue` hooks already have one.
 */
export const useFlagsVersion = (): number =>
  useSyncExternalStore(subscribeToFlagChanges, getFlagsVersion);
